import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryClient } from '@/routes/__root'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { LoraPopup } from '@/components/lora-popup'
import { toast } from 'sonner'
import { Loader2, Download, Search, ExternalLink } from 'lucide-react'
import type { LoraPreviewDTO, BaseModelFamilyDTO, LoraDetailDTO } from '@/lib/types'

export const Route = createFileRoute('/loras/')({
  beforeLoad: async () => {
    const user = queryClient.getQueryData(['user'])
    if (!user) {
      try {
        await queryClient.ensureQueryData({ queryKey: ['user'], queryFn: api.auth.getCurrentUser })
      } catch (error) {
        throw redirect({ to: '/auth/login', search: { redirect: '/loras' } })
      }
    }
  },
  component: LorasPage,
})

function LorasPage() {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedFamily, setSelectedFamily] = useState<string>('all')
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [selectedLoraId, setSelectedLoraId] = useState<number | null>(null)
  const [loraPopupOpen, setLoraPopupOpen] = useState(false)

  // Fetch model families for filtering and import
  const { data: familiesData } = useQuery({
    queryKey: ['loras', 'families'],
    queryFn: api.loras.families,
  })

  // Fetch loras with filters
  const { data: lorasData, isLoading } = useQuery({
    queryKey: ['loras', 'list', selectedFamily, searchTerm],
    queryFn: () =>
      api.loras.list({
        baseModelFamily: selectedFamily === 'all' ? undefined : selectedFamily,
        search: searchTerm || undefined,
        limit: 100,
      }),
  })

  // Fetch lora detail when one is selected
  const { data: loraDetail, isLoading: isLoadingDetail } = useQuery({
    queryKey: ['loras', 'detail', selectedLoraId],
    queryFn: () => api.loras.detail(selectedLoraId!),
    enabled: !!selectedLoraId && loraPopupOpen,
  })

  const families: BaseModelFamilyDTO[] = (familiesData as BaseModelFamilyDTO[] | undefined) ?? []

  const handleLoraClick = (loraId: number) => {
    setSelectedLoraId(loraId)
    setLoraPopupOpen(true)
  }

  const handleUseOnGeneration = (loraId: number) => {
    // Navigate to generation page with the lora ID as a search param
    navigate({
      to: '/generation',
      search: {
        loraId,
        modelSlug: undefined,
        prompt: undefined,
        width: undefined,
        height: undefined,
        steps: undefined,
        cfgScale: undefined,
        clipSkip: undefined,
        seedMode: undefined,
        seed: undefined,
        loras: undefined,
        workflowVariant: undefined,
      }
    })
  }

  const queryClientInstance = useQueryClient()
  const deleteMutation = useMutation({
    mutationFn: api.loras.delete,
    onSuccess: (data) => {
      toast.success(data.message)
      queryClientInstance.invalidateQueries({ queryKey: ['loras'] })
      queryClientInstance.invalidateQueries({ queryKey: ['generation', 'onload'] })
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to delete LoRA')
    },
  })

  const handleDelete = async (loraId: number) => {
    await deleteMutation.mutateAsync(loraId)
  }

  const handleUpdateNotes = async (loraId: number, notes: string) => {
    // Update the API in the background
    await api.loras.updateNotes(loraId, notes)
  }

  const handleLocalNotesUpdate = (loraId: number, notes: string) => {
    // Optimistically update the lora detail in cache
    queryClientInstance.setQueryData(
      ['loras', 'detail', loraId],
      (oldData: LoraDetailDTO | undefined) => {
        if (!oldData) return oldData
        return { ...oldData, notes }
      }
    )

    // Also update the lora in the list cache
    queryClientInstance.setQueryData(
      ['loras', 'list', selectedFamily, searchTerm],
      (oldData: { items: LoraPreviewDTO[]; total: number } | undefined) => {
        if (!oldData) return oldData
        return {
          ...oldData,
          items: oldData.items.map(lora =>
            lora.id === loraId ? { ...lora, notes } : lora
          )
        }
      }
    )
  }

  return (
    <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">LoRA Library</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage and import LoRAs from CivitAI
          </p>
        </div>
        <ImportLoraDialog
          open={importDialogOpen}
          onOpenChange={setImportDialogOpen}
          families={families}
        />
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-end">
        <div className="flex-1">
          <Label htmlFor="search">Search</Label>
          <div className="relative mt-1.5">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="search"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
        <div className="w-64">
          <Label htmlFor="base-model">Model Family</Label>
          <Select value={selectedFamily} onValueChange={setSelectedFamily}>
            <SelectTrigger id="base-model" className="mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Families</SelectItem>
              {families.map((family) => (
                <SelectItem key={family.family} value={family.family}>
                  {family.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Loras Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : lorasData && lorasData.items.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
          {lorasData.items.map((lora) => (
            <LoraCard key={lora.id} lora={lora} onClick={() => handleLoraClick(lora.id)} />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground mb-4">
              {searchTerm || selectedFamily !== 'all'
                ? 'No LoRAs found matching your filters'
                : 'No LoRAs imported yet'}
            </p>
            <Button onClick={() => setImportDialogOpen(true)}>
              <Download className="mr-2 h-4 w-4" />
              Import Your First LoRA
            </Button>
          </CardContent>
        </Card>
      )}

      {lorasData && lorasData.total > 0 && (
        <p className="text-sm text-muted-foreground text-center">
          Showing {lorasData.items.length} of {lorasData.total} LoRAs
        </p>
      )}

      <LoraPopup
        open={loraPopupOpen}
        onOpenChange={setLoraPopupOpen}
        lora={loraDetail as LoraDetailDTO | undefined}
        isLoading={isLoadingDetail}
        onUseOnGeneration={handleUseOnGeneration}
        onDelete={handleDelete}
        onUpdateNotes={handleUpdateNotes}
        onLocalNotesUpdate={handleLocalNotesUpdate}
      />
    </div>
  )
}

function LoraCard({ lora, onClick }: { lora: LoraPreviewDTO; onClick: () => void }) {
  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" onClick={onClick}>
      <CardHeader className="p-0">
        {lora.thumbnail_url ? (
          <div className="relative w-full pt-[100%] overflow-hidden bg-muted">
            <img
              src={lora.thumbnail_url}
              alt={lora.display_name}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="relative w-full pt-[100%] bg-muted flex items-center justify-center">
            <p className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm">No preview</p>
          </div>
        )}
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        <div>
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="font-semibold text-lg leading-tight flex-1">{lora.display_name}</h3>
            <Badge variant="outline" className="text-xs shrink-0">
              {lora.base_model}
            </Badge>
          </div>
          {lora.default_strength && (
            <p className="text-xs text-muted-foreground">
              Default strength: {lora.default_strength}
            </p>
          )}
        </div>

        {lora.trigger_words && lora.trigger_words.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {lora.trigger_words.slice(0, 3).map((word, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs">
                {word}
              </Badge>
            ))}
            {lora.trigger_words.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{lora.trigger_words.length - 3} more
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function ImportLoraDialog({
  open,
  onOpenChange,
  families,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  families: BaseModelFamilyDTO[]
}) {
  const queryClient = useQueryClient()
  const [url, setUrl] = useState('')
  const [selectedFamily, setSelectedFamily] = useState('')
  const [expectDownload, setExpectDownload] = useState(true)

  const importMutation = useMutation({
    mutationFn: api.loras.import,
    onSuccess: (data) => {
      toast.success(`Successfully imported ${data.lora.display_name}!`)
      queryClient.invalidateQueries({ queryKey: ['loras'] })
      queryClient.invalidateQueries({ queryKey: ['generation', 'onload'] })
      setUrl('')
      setSelectedFamily('')
      setExpectDownload(true)
      onOpenChange(false)
    },
    onError: (error: any) => {
      const message = error?.message || 'Failed to import LoRA'
      toast.error(message)
    },
  })

  const handleImport = () => {
    if (!url.trim()) {
      toast.error('Please enter a CivitAI URL')
      return
    }
    if (!selectedFamily) {
      toast.error('Please select a model family')
      return
    }

    importMutation.mutate({
      url: url.trim(),
      base_model_family: selectedFamily,
      expect_download: expectDownload,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Download className="mr-2 h-4 w-4" />
          Import LoRA
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Import LoRA from CivitAI</DialogTitle>
          <DialogDescription>
            Enter a CivitAI model URL to import a LoRA. Make sure to download the file from CivitAI
            first - it should be in your Downloads folder.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="civitai-url">CivitAI URL</Label>
            <div className="flex gap-2">
              <Input
                id="civitai-url"
                placeholder="https://civitai.com/models/..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={importMutation.isPending}
              />
              {url && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => window.open(url, '_blank')}
                  disabled={!url.startsWith('http')}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Example: https://civitai.com/models/123456/model-name
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="base-model-select">Model Family</Label>
            <Select
              value={selectedFamily}
              onValueChange={setSelectedFamily}
              disabled={importMutation.isPending}
            >
              <SelectTrigger id="base-model-select">
                <SelectValue placeholder="Select a model family..." />
              </SelectTrigger>
              <SelectContent>
                {families.map((family) => (
                  <SelectItem key={family.family} value={family.family}>
                    {family.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Choose which model family this LoRA is compatible with (Flux or Qwen)
            </p>
          </div>

          <div className="bg-muted/50 p-3 rounded-lg space-y-2">
            <p className="text-sm font-medium">Before importing:</p>
            <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
              <li>Visit the CivitAI URL above</li>
              <li>Download the LoRA file to your Downloads folder</li>
              <li>Return here and click "Import"</li>
            </ol>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={importMutation.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={importMutation.isPending}>
            {importMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Import
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
